/** Collects rich punch metadata: GPS (mandatory for clock-in), device, browser, fingerprint. */

const FP_KEY = "acq_device_fp";

export function deviceFingerprint() {
  let fp = localStorage.getItem(FP_KEY);
  if (!fp) {
    fp = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(FP_KEY, fp);
  }
  return fp;
}

function parseUA() {
  const ua = navigator.userAgent;
  let browser = "Unknown";
  if (/edg\//i.test(ua)) browser = "Edge";
  else if (/opr\//i.test(ua)) browser = "Opera";
  else if (/chrome|crios/i.test(ua)) browser = "Chrome";
  else if (/firefox|fxios/i.test(ua)) browser = "Firefox";
  else if (/safari/i.test(ua)) browser = "Safari";
  let os = "Unknown";
  if (/windows/i.test(ua)) os = "Windows";
  else if (/android/i.test(ua)) os = "Android";
  else if (/iphone|ipad|ipod/i.test(ua)) os = "iOS";
  else if (/mac os/i.test(ua)) os = "macOS";
  else if (/linux/i.test(ua)) os = "Linux";
  const mobile = /mobi|android|iphone|ipod/i.test(ua);
  const tablet = /ipad|tablet/i.test(ua);
  return { browser, os, device_type: tablet ? "tablet" : mobile ? "mobile" : "desktop" };
}

function getPosition(timeoutMs = 12000) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error("Geolocation is not supported by this browser")); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 60000 }
    );
  });
}

async function reverseGeocode(lat, lng) {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 4000);
    const r = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
      { signal: ctrl.signal });
    clearTimeout(t);
    if (!r.ok) return null;
    const d = await r.json();
    return [d.locality, d.city, d.principalSubdivision, d.countryName].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(", ") || null;
  } catch { return null; }
}

/** Returns punch payload. Throws Error with user-friendly message when location is required but denied. */
export async function collectPunchMeta({ requireLocation = false } = {}) {
  const ua = parseUA();
  const meta = {
    ...ua,
    screen: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    device_fingerprint: deviceFingerprint(),
    platform: ua.device_type === "desktop" ? "web" : "mobile_web",
  };
  try {
    const pos = await getPosition();
    meta.lat = pos.coords.latitude;
    meta.lng = pos.coords.longitude;
    meta.accuracy_m = Math.round(pos.coords.accuracy || 0);
    meta.address = await reverseGeocode(meta.lat, meta.lng);
  } catch (err) {
    if (requireLocation) {
      const denied = err && err.code === 1;
      throw new Error(denied
        ? "Location access is mandatory to clock in. Please allow location permission in your browser and try again."
        : "Couldn't get your location. Location access is mandatory to clock in — please enable it and retry.");
    }
  }
  return meta;
}
